import { queryKeys } from '@/lib/query-keys';
import { apiClient } from '@borradh-workspace/api-client';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';

export interface DeleteContentVersionInput {
  /** The content item the version belongs to. */
  itemId: string;
  /** The version to remove (the graphic that version holds). */
  graphicId: string;
}

export interface DeleteContentVersionResult {
  /** The version to show now, or null when the whole graphic went. */
  fallbackAssetId: string | null;
  deletedWholeGraphic: boolean;
}

/**
 * Delete one version of an uploaded photo's edits or a generated graphic.
 * The newest remaining version takes over; the last version of a generated
 * graphic deletes the graphic.
 */
export const useDeleteContentVersion = (options?: {
  onSuccess?: (result: DeleteContentVersionResult) => void;
}) => {
  const queryClient = useQueryClient();
  const mutation = useMutation({
    mutationFn: ({ itemId, graphicId }: DeleteContentVersionInput) =>
      apiClient.delete<DeleteContentVersionResult>(
        `content-batches/items/${itemId}/versions/${graphicId}`
      ),
    onSuccess: async (result) => {
      await queryClient.invalidateQueries({
        queryKey: queryKeys.content.allVersions(),
      });
      void queryClient.invalidateQueries({
        queryKey: queryKeys.graphics.all(),
      });
      toast.success(
        result.deletedWholeGraphic ? 'Graphic deleted' : 'Version deleted'
      );
      options?.onSuccess?.(result);
    },
    onError: (error: Error) => {
      toast.error(error.message || 'The version could not be deleted');
    },
  });
  return {
    deleteContentVersion: mutation.mutate,
    isDeletingVersion: mutation.isPending,
  };
};
